import { useRestricted } from "@core/org";
// Libellés, statuts (icône + mot) et petits outils partagés par les pages du module pro.
import { useState, type ReactNode } from "react";
import { Notice, StatusChip, useToast, type CheckStatus } from "@core/ui";
import { useGet } from "@core/hooks";

export const TRADES: Record<string, string> = {
  plomberie: "Plomberie",
  electricite: "Électricité",
  chauffage: "Chauffage",
  serrurerie: "Serrurerie",
  menuiserie: "Menuiserie",
  peinture: "Peinture",
  maconnerie: "Maçonnerie",
  couverture: "Couverture",
  vitrerie: "Vitrerie",
  nettoyage: "Nettoyage",
  espaces_verts: "Espaces verts",
  multiservice: "Multiservice",
  autre: "Autre",
};

export const OWNER_KINDS: Record<string, string> = { personne: "Personne", sci: "SCI", indivision: "Indivision", societe: "Société" };

export const REGIMES: Record<string, string> = {
  vide: "Location vide",
  meuble: "Location meublée",
  colocation: "Colocation",
  mobilite: "Bail mobilité",
  etudiant: "Bail étudiant",
  commercial: "Bail commercial",
  professionnel: "Bail professionnel",
};

export const LEASE_STATUS: Record<string, string> = {
  brouillon: "Brouillon",
  en_signature: "En signature",
  signe: "Signé",
  actif: "Actif",
  preavis: "Préavis",
  termine: "Terminé",
  resilie: "Résilié",
};

export const PRIORITIES: Record<string, string> = { basse: "Basse", normale: "Normale", haute: "Haute" };

export const EVENT_KINDS: Record<string, string> = {
  echeance_triennale: "Échéance triennale",
  date_limite_conge_preneur: "Congé du preneur",
  date_limite_conge_bailleur: "Congé du bailleur",
  demande_renouvellement: "Renouvellement",
  indexation: "Indexation",
  fin_de_bail: "Terme du bail",
};

type StatusMap = Record<string, [CheckStatus, string]>;

const MAPS = {
  expense: {
    non_requise: ["info", "Sans accord requis"],
    en_attente: ["a_confirmer", "Accord attendu"],
    approuvee: ["conforme", "Approuvée"],
    refusee: ["anomalie", "Refusée"],
    expiree: ["manquant", "Sans réponse"],
    engagee: ["conforme", "Engagée"],
    annulee: ["non_concluant", "Annulée"],
  },
  insurance: {
    a_jour: ["conforme", "À jour selon l’attestation"],
    a_renouveler: ["a_confirmer", "À renouveler sous 30 jours"],
    expiree: ["anomalie", "Attestation expirée"],
    non_renseignee: ["manquant", "Attestation non renseignée"],
  },
  delivery: { reussi: ["conforme", "Livrée"], echec: ["anomalie", "Échec"] },
  bulk: { a_envoyer: ["a_confirmer", "À envoyer"], envoye: ["conforme", "Envoyé"], ignore: ["manquant", "Non envoyé"], echec: ["anomalie", "Échec"] },
  bulkAction: { apercu: ["a_confirmer", "Aperçu, rien envoyé"], termine: ["conforme", "Terminé"], partiel: ["anomalie", "Échecs à reprendre"] },
  importRow: { valide: ["conforme", "Valide"], erreur: ["anomalie", "Erreur"], doublon: ["info", "Déjà importé"], importe: ["conforme", "Importé"] },
  importStatus: { apercu: ["a_confirmer", "Aperçu"], applique: ["conforme", "Appliqué"], annule: ["non_concluant", "Annulé"] },
  importPiece: { a_rattacher: ["a_confirmer", "À rattacher"], rattachee: ["conforme", "Rattachée"], doublon: ["info", "Déjà rattachée"], erreur: ["anomalie", "Refusée"] },
  event: { a_venir: ["a_confirmer", "À venir"], traite: ["conforme", "Traité"], ignore: ["non_concluant", "Ignoré"] },
  task: { a_faire: ["a_confirmer", "À faire"], en_cours: ["info", "En cours"], fait: ["conforme", "Fait"], annule: ["non_concluant", "Annulé"] },
  review: { a_valider: ["a_confirmer", "Modèle à faire valider"], valide: ["conforme", "Relecture déclarée"] },
  invoice: { emise: ["conforme", "Émise"], annulee: ["non_concluant", "Annulée par avoir"] },
  access: { active: ["conforme", "Accès actif"], pending: ["a_confirmer", "Invitation envoyée"], revoked: ["non_concluant", "Accès retiré"], expired: ["manquant", "Invitation expirée"] },
} satisfies Record<string, StatusMap>;

/** Statut métier affiché avec son icône et son mot (jamais la couleur seule). */
export function Status({ kind, value }: { kind: keyof typeof MAPS; value: string | null | undefined }) {
  const map = MAPS[kind] as StatusMap;
  const [s, label] = (value && map[value]) || ["info", value ?? "—"];
  return <StatusChip status={s} label={label} />;
}

export function accessState(a: { accepted_at?: string | null; revoked_at?: string | null; expires_at?: string | null }): string {
  if (a.revoked_at) return "revoked";
  if (a.accepted_at) return "active";
  if (a.expires_at && new Date(a.expires_at) < new Date()) return "expired";
  return "pending";
}

/** Exécute une action et garde l'erreur pour l'afficher près du formulaire. */
export function useRun() {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  async function run<T>(fn: () => Promise<T>, success?: string): Promise<T | undefined> {
    setBusy(true);
    setError(null);
    try {
      const r = await fn();
      if (success) toast(success);
      return r;
    } catch (e) {
      setError(e);
      return undefined;
    } finally {
      setBusy(false);
    }
  }
  return { busy, error, run, setError };
}

/** Action ponctuelle d'une ligne de tableau : message de réussite ou d'erreur en notification. */
export function useQuick() {
  const toast = useToast();
  return async (fn: () => Promise<unknown>, success: string) => {
    try {
      await fn();
      toast(success);
      return true;
    } catch (e) {
      toast(e instanceof Error ? e.message : "Action impossible.", "erreur");
      return false;
    }
  };
}

export function RuleNotice({ children }: { children?: ReactNode }) {
  return (
    <Notice tone="attention" title="Règle proposée, à faire valider">
      {children ?? "Les règles juridiques et fiscales des baux commerciaux et professionnels affichées ici sont des propositions : faites-les valider par un professionnel du droit avant de vous y fier."}
    </Notice>
  );
}

export type Option = { value: string; label: string };

/** Logements visibles pour l'utilisateur (périmètre respecté par le serveur). */
export function usePropertyOptions(orgId: string, placeholder = "Choisir un logement"): { options: Option[]; items: any[] } {
  const q = useGet<{ items: any[] }>(`/api/v1/orgs/${orgId}/pro/properties`);
  const items = q.data?.items ?? [];
  return { items, options: [{ value: "", label: placeholder }, ...items.map((p) => ({ value: p.id, label: p.owner_name ? `${p.label} · ${p.owner_name}` : p.label }))] };
}

export function useOwnerOptions(orgId: string, placeholder = "Choisir un mandant"): Option[] {
  // Liste refusée à un collaborateur restreint (F-091) : le mandant reste celui du logement.
  const restricted = useRestricted();
  const q = useGet<{ items: any[] }>(restricted ? null : `/api/v1/orgs/${orgId}/pro/owners`);
  return [{ value: "", label: placeholder }, ...(q.data?.items ?? []).map((o) => ({ value: o.id, label: o.display_name }))];
}

export function useMemberOptions(orgId: string, placeholder = "Choisir une personne"): Option[] {
  const q = useGet<{ items: any[] }>(`/api/v1/orgs/${orgId}/members`);
  return [{ value: "", label: placeholder }, ...(q.data?.items ?? []).map((m) => ({ value: m.user_id, label: m.full_name }))];
}

export function docUrl(id: string) {
  return `/api/v1/documents/${id}/fichier`;
}

export function thisMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** Pourcentage saisi (« 5 », « 5,5 ») en points de base. */
export function parsePercentBp(s: string): number | null {
  const v = s.trim().replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(v)) return null;
  return Math.round(parseFloat(v) * 100);
}
